/**
 * The batch date window for the whole assignment, plus the activation choice.
 *
 * Dates are optional: without them the assignment takes effect now. With a
 * future start, statuses are left alone until that date arrives — the note
 * under the fields says so rather than silently doing nothing.
 */

import { CalendarDays, Check } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatLongDate, formatDayRange } from '@/lib/unitCodeParser';
import type { DateWindow } from './types';

interface BatchDateFieldsProps {
  batch: DateWindow;
  onChange: (field: 'start' | 'end', value: string) => void;
  overrideCount: number;
  /** Units this assignment would switch to Active right now. */
  activatableCount: number;
  /** Earliest future start among staged units, when nothing activates today. */
  nextFutureStart: string | null;
  setActive: boolean;
  onToggleActive: () => void;
}

function DateBlock({
  label,
  value,
  onPick,
  onClear,
}: {
  label: string;
  value: string;
  onPick: (iso: string) => void;
  onClear: () => void;
}) {
  const isSet = Boolean(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative flex h-16 min-w-0 flex-1 flex-row items-center gap-3 rounded-lg px-3.5 text-left transition-[background-color,border-color] hover:border-[#8FB560] hover:brightness-110 ${
            isSet
              ? 'border border-[#8FB560]/45 bg-[#8FB560]/[0.08]'
              : 'border border-dashed border-muted-foreground/50'
          }`}
        >
          <CalendarDays
            className={`h-[34px] w-[34px] flex-none ${isSet ? 'text-[#8FB560]' : 'text-muted-foreground/50'}`}
            strokeWidth={1.6}
          />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
            <span className="text-[9px] font-medium uppercase tracking-wider text-muted-foreground">
              {label}
            </span>
            <span
              className={`whitespace-nowrap text-[15px] font-semibold tabular-nums tracking-tight ${
                isSet ? 'text-foreground' : 'text-muted-foreground/80'
              }`}
            >
              {isSet ? formatLongDate(value) : 'Not set'}
            </span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value ? parseISO(value) : undefined}
          onSelect={(d) => onPick(d ? format(d, 'yyyy-MM-dd') : '')}
          initialFocus
        />
        <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2">
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {isSet ? formatLongDate(value) : `Pick ${label.toLowerCase()} date`}
          </span>
          <button
            type="button"
            onClick={onClear}
            className="h-6 flex-none rounded-md border border-border px-2 text-[11px] text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function BatchDateFields({
  batch,
  onChange,
  overrideCount,
  activatableCount,
  nextFutureStart,
  setActive,
  onToggleActive,
}: BatchDateFieldsProps) {
  const hasNote = Boolean(batch.start || batch.end || overrideCount);

  return (
    <div className="flex flex-col gap-1.5 border-t border-border pt-3.5">
      <div className="flex min-h-[26px] items-center justify-between gap-3">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Dates · optional
        </span>

        {activatableCount > 0 ? (
          <button
            type="button"
            onClick={onToggleActive}
            role="checkbox"
            aria-checked={setActive}
            className="-mx-1.5 -my-1 flex flex-none items-center gap-[7px] rounded-md px-1.5 py-1 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span
              aria-hidden="true"
              className={`flex h-3.5 w-3.5 flex-none items-center justify-center rounded ${
                setActive ? 'border border-[#8FB560] bg-[#8FB560]' : 'border border-muted-foreground/50'
              }`}
            >
              {setActive && <Check className="h-[11px] w-[11px] text-black" strokeWidth={3.5} />}
            </span>
            <span className="whitespace-nowrap text-[11px] text-muted-foreground">
              Set {activatableCount} {activatableCount === 1 ? 'unit' : 'units'} to Active
            </span>
          </button>
        ) : nextFutureStart ? (
          <span className="flex-none whitespace-nowrap text-[11px] tabular-nums text-muted-foreground/70">
            Starts {formatLongDate(nextFutureStart)} · statuses unchanged until then
          </span>
        ) : null}
      </div>

      <div className="flex gap-2">
        <DateBlock
          label="Start"
          value={batch.start}
          onPick={(iso) => onChange('start', iso)}
          onClear={() => onChange('start', '')}
        />
        <DateBlock
          label="End"
          value={batch.end}
          onPick={(iso) => onChange('end', iso)}
          onClear={() => onChange('end', '')}
        />
      </div>

      {hasNote && (
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {[
            formatDayRange(batch.start, batch.end) || 'No batch dates',
            overrideCount ? `${overrideCount} ${overrideCount === 1 ? 'override' : 'overrides'}` : '',
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      )}
    </div>
  );
}
